import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft,
  User,
  Phone,
  Mail,
  Building2,
  Car,
  Plus,
  Edit,
  Trash2,
  Wrench,
  MessageCircle,
  Calendar,
  MoreVertical,
  UserX,
  UserCheck
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const carBrands = [
  'Nissan', 'Chevrolet', 'Volkswagen', 'Toyota', 'Ford', 'Honda', 'Mazda', 
  'Kia', 'Hyundai', 'Suzuki', 'Renault', 'Jeep', 'Dodge', 'Ram', 'BMW', 
  'Mercedes-Benz', 'Audi', 'Seat', 'Peugeot', 'Mitsubishi', 'Otro'
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [client, setClient] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit client modal
  const [showEditClient, setShowEditClient] = useState(false);
  const [clientFormData, setClientFormData] = useState({
    name: '',
    whatsapp: '',
    email: '',
    company: '',
    notes: '',
    is_prospect: false
  });
  
  // Vehicle modal
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleFormData, setVehicleFormData] = useState({
    brand: '',
    model: '',
    year: currentYear,
    plates: '',
    color: '',
    vin: ''
  });

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      // Fetch client
      const clientRes = await fetch(`${API_URL}/api/clients/${clientId}`, {
        credentials: 'include'
      });
      if (!clientRes.ok) {
        throw new Error('Cliente no encontrado');
      }
      const clientData = await clientRes.json();
      setClient(clientData);
      setClientFormData({
        name: clientData.name,
        whatsapp: clientData.whatsapp || '',
        email: clientData.email || '',
        company: clientData.company || '',
        notes: clientData.notes || '',
        is_prospect: clientData.is_prospect
      });

      // Fetch vehicles for this client (only if not prospect)
      if (!clientData.is_prospect) {
        const vehiclesRes = await fetch(`${API_URL}/api/vehicles?client_id=${clientId}`, {
          credentials: 'include'
        });
        if (vehiclesRes.ok) {
          setVehicles(await vehiclesRes.json());
        }

        // Fetch services for this client
        const servicesRes = await fetch(`${API_URL}/api/services`, {
          credentials: 'include'
        });
        if (servicesRes.ok) {
          const allServices = await servicesRes.json();
          setServices(allServices.filter(s => s.client_id === clientId));
        }
      }
    } catch (error) {
      toast.error(error.message);
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(clientFormData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar');
      }

      toast.success('Cliente actualizado');
      setShowEditClient(false);
      fetchClientData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteClient = async () => {
    if (!confirm('¿Eliminar este cliente y todos sus vehículos y servicios?')) return;

    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Cliente eliminado');
      navigate('/clients');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveVehicle = async (e) => {
    e.preventDefault();
    try {
      const url = editingVehicle 
        ? `${API_URL}/api/vehicles/${editingVehicle.vehicle_id}`
        : `${API_URL}/api/vehicles`;
      
      const response = await fetch(url, {
        method: editingVehicle ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...vehicleFormData,
          client_id: clientId,
          year: parseInt(vehicleFormData.year)
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar');
      }

      toast.success(editingVehicle ? 'Vehículo actualizado' : 'Vehículo creado');
      setShowVehicleForm(false);
      setEditingVehicle(null);
      resetVehicleForm();
      fetchClientData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!confirm('¿Eliminar este vehículo y sus servicios?')) return;

    try {
      const response = await fetch(`${API_URL}/api/vehicles/${vehicleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Vehículo eliminado');
      fetchClientData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetVehicleForm = () => {
    setVehicleFormData({
      brand: '',
      model: '',
      year: currentYear,
      plates: '',
      color: '',
      vin: ''
    });
  };

  const openWhatsApp = (phone) => {
    if (!phone) return;
    // Use country code from user profile
    const countryCode = user?.country_code || '+52';
    let cleanPhone = phone.replace(/\D/g, '');
    // If phone doesn't start with country code, add it
    if (!cleanPhone.startsWith(countryCode.replace('+', ''))) {
      cleanPhone = countryCode.replace('+', '') + cleanPhone;
    }
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const statusLabels = {
    reception: 'Recepción',
    in_progress: 'En Progreso',
    completed: 'Completado',
    delivered: 'Entregado'
  };

  if (loading) {
    return (
      <div className="animate-fade-in">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="animate-fade-in" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate('/clients')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">{client.name}</h1>
            {client.is_prospect && (
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                Prospecto
              </span>
            )}
          </div>
          <p className="text-slate-500">
            {client.is_prospect ? 'Prospecto' : `${vehicles.length} vehículo${vehicles.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowEditClient(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
          <Button variant="outline" onClick={handleDeleteClient} className="text-red-600 hover:text-red-700">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Client Info Card */}
      <div className="card-base p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            client.is_prospect ? 'bg-slate-100' : 'bg-blue-100'
          }`}>
            {client.is_prospect ? (
              <UserX className="w-8 h-8 text-slate-500" />
            ) : (
              <UserCheck className="w-8 h-8 text-blue-600" />
            )}
          </div>
          
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            {client.whatsapp && (
              <button 
                onClick={() => openWhatsApp(client.whatsapp)}
                className="flex items-center gap-2 text-[#25D366] hover:underline"
              >
                <MessageCircle className="w-4 h-4" />
                {client.whatsapp}
              </button>
            )}
            {client.email && (
              <a href={`mailto:${client.email}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
                <Mail className="w-4 h-4" />
                {client.email}
              </a>
            )}
            {client.company && (
              <span className="flex items-center gap-2 text-slate-600">
                <Building2 className="w-4 h-4" />
                {client.company}
              </span>
            )}
            <span className="flex items-center gap-2 text-slate-500 text-sm">
              <Calendar className="w-4 h-4" />
              Cliente desde {new Date(client.created_at).toLocaleDateString('es-MX')}
            </span>
          </div>
        </div>
        
        {client.notes && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">Notas</p>
            <p className="text-slate-700">{client.notes}</p>
          </div>
        )}
      </div>

      {/* Vehicles Section - Only for clients, not prospects */}
      {!client.is_prospect && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900">Vehículos</h2>
            <Button onClick={() => setShowVehicleForm(true)} className="bg-[#1E3A5F]">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Vehículo
            </Button>
          </div>

          {vehicles.length > 0 ? (
            <div className="space-y-3">
              {vehicles.map((vehicle) => (
                <div key={vehicle.vehicle_id} className="card-base card-hover p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Car className="w-6 h-6 text-emerald-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">
                        {vehicle.brand} {vehicle.model} <span className="text-slate-500 font-normal">{vehicle.year}</span>
                      </p>
                      <div className="flex flex-wrap gap-x-4 text-sm text-slate-500">
                        <span className="font-mono font-medium text-slate-700">{vehicle.plates}</span>
                        {vehicle.color && <span>{vehicle.color}</span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link 
                        to={`/services/new?vehicle_id=${vehicle.vehicle_id}`}
                        className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 transition-colors"
                      >
                        <Wrench className="w-4 h-4 inline mr-1" />
                        Servicio
                      </Link>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setEditingVehicle(vehicle);
                            setVehicleFormData({
                              brand: vehicle.brand,
                              model: vehicle.model,
                              year: vehicle.year,
                              plates: vehicle.plates,
                              color: vehicle.color || '',
                              vin: vehicle.vin || ''
                            });
                            setShowVehicleForm(true);
                          }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteVehicle(vehicle.vehicle_id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-base p-8 text-center">
              <Car className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">No hay vehículos registrados</p>
              <Button onClick={() => setShowVehicleForm(true)} className="bg-[#1E3A5F]">
                <Plus className="w-4 h-4 mr-2" />
                Agregar vehículo
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Services History - Only for clients */}
      {!client.is_prospect && services.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Historial de Servicios</h2>
          <div className="space-y-3">
            {services.map((service) => {
              const vehicle = vehicles.find(v => v.vehicle_id === service.vehicle_id);
              return (
                <Link 
                  key={service.service_id}
                  to={`/services/${service.service_id}`}
                  className="card-base card-hover p-4 block"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{service.description}</p>
                      <p className="text-sm text-slate-500">
                        {vehicle?.brand} {vehicle?.model} • {new Date(service.created_at).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium status-${service.status}`}>
                        {statusLabels[service.status]}
                      </span>
                      <p className="text-sm font-bold text-emerald-600 mt-1">
                        ${service.profit?.toLocaleString('es-MX')}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Prospect Notice */}
      {client.is_prospect && (
        <div className="card-base p-8 text-center border-dashed border-2">
          <UserX className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium mb-2">Este es un prospecto</p>
          <p className="text-slate-500 text-sm mb-4">
            Los prospectos no pueden tener vehículos ni servicios asociados.
            Convierte a esta persona en cliente para registrar sus vehículos.
          </p>
          <Button 
            onClick={() => {
              setClientFormData({ ...clientFormData, is_prospect: false });
              setShowEditClient(true);
            }}
            className="bg-[#1E3A5F]"
          >
            Convertir a Cliente
          </Button>
        </div>
      )}

      {/* Edit Client Modal */}
      <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateClient} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={clientFormData.name}
                onChange={(e) => setClientFormData({ ...clientFormData, name: e.target.value })}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="whatsapp">WhatsApp (10 dígitos)</Label>
                <Input
                  id="whatsapp"
                  value={clientFormData.whatsapp}
                  onChange={(e) => setClientFormData({ ...clientFormData, whatsapp: e.target.value })}
                  placeholder="5512345678"
                  maxLength={10}
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={clientFormData.email}
                  onChange={(e) => setClientFormData({ ...clientFormData, email: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="company">Empresa</Label>
              <Input
                id="company"
                value={clientFormData.company}
                onChange={(e) => setClientFormData({ ...clientFormData, company: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={clientFormData.notes}
                onChange={(e) => setClientFormData({ ...clientFormData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditClient(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#1E3A5F]">
                Guardar Cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vehicle Form Modal */}
      <Dialog open={showVehicleForm} onOpenChange={(open) => {
        if (!open) {
          setShowVehicleForm(false);
          setEditingVehicle(null);
          resetVehicleForm();
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingVehicle ? 'Editar Vehículo' : 'Nuevo Vehículo'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveVehicle} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="brand">Marca *</Label>
                <Select
                  value={vehicleFormData.brand}
                  onValueChange={(value) => setVehicleFormData({ ...vehicleFormData, brand: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar marca" />
                  </SelectTrigger>
                  <SelectContent>
                    {carBrands.map((brand) => (
                      <SelectItem key={brand} value={brand}>
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="model">Modelo *</Label>
                <Input
                  id="model"
                  value={vehicleFormData.model}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, model: e.target.value })}
                  placeholder="Ej: Sentra, Aveo"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="year">Año *</Label>
                <Select
                  value={vehicleFormData.year.toString()}
                  onValueChange={(value) => setVehicleFormData({ ...vehicleFormData, year: parseInt(value) })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Año" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="plates">Placas *</Label>
                <Input
                  id="plates"
                  value={vehicleFormData.plates}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, plates: e.target.value.toUpperCase() })}
                  placeholder="ABC-123"
                  required
                  className="font-mono uppercase"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="color">Color</Label>
                <Input
                  id="color"
                  value={vehicleFormData.color}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, color: e.target.value })}
                  placeholder="Ej: Blanco, Negro"
                />
              </div>
              <div>
                <Label htmlFor="vin">VIN / No. Serie</Label>
                <Input
                  id="vin"
                  value={vehicleFormData.vin}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, vin: e.target.value.toUpperCase() })}
                  placeholder="17 caracteres"
                  maxLength={17}
                  className="font-mono uppercase"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowVehicleForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#1E3A5F]">
                {editingVehicle ? 'Guardar Cambios' : 'Crear Vehículo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
